import React from "react"
import {
  Box,
  VStack,
  Text,
  Button,
  Heading,
  Progress,
  HStack,
  Link,
} from "@chakra-ui/react"
import { Api } from "../lib/api"
import Papa from "papaparse"
import { addAlert, AlertType, removeAlertBySource } from "../lib/alerts"
import {
  JobStatus,
  FileName,
  FileError,
  UploadProgress,
  IsUploading,
  UploadStatus,
  CurrentOrg,
} from "@/lib/store"
import { useParams } from "react-router-dom"
import useFileStore from "../lib/fileStore"
import { getPlanLimits, CONTACT_SALES_URL } from "@/lib/limits"

function countRecordsFromParsed(fileType, body) {
  if (fileType === "text/csv") {
    const parsed = Papa.parse(body, { header: false, skipEmptyLines: true })
    return Math.max(0, (parsed.data?.length || 0) - 1)
  }
  try {
    const json = JSON.parse(body)
    return Array.isArray(json) ? json.length : 1
  } catch {
    return 0
  }
}

export default function UploadRecords() {
  const { schemaId } = useParams()
  const fileInputRef = React.useRef(null)
  const api = React.useMemo(() => new Api(), [])
  const [currentOrg] = CurrentOrg.useState()
  const [fileIndex, setFileIndex] = React.useState(0)
  const [fileTotal, setFileTotal] = React.useState(0)

  const [jobStatus, setJobStatus] = JobStatus.useState()
  const [fileName, setFileName] = FileName.useState()
  const [fileError, setFileError] = FileError.useState()
  const [uploadProgress, setUploadProgress] = UploadProgress.useState()
  const [isUploading, setIsUploading] = IsUploading.useState()

  const { selectedFile, selectedFiles, setSelectedFiles, clearSelectedFile } =
    useFileStore()

  React.useEffect(() => {
    return () => {
      setIsUploading(UploadStatus.NONE)
      setUploadProgress(0)
      setJobStatus(null)
      setFileName("")
      setFileError(null)
      if (selectedFile || (selectedFiles && selectedFiles.length)) {
        clearSelectedFile()
      }
    }
  }, [])

  function openAttachment() {
    fileInputRef.current.click()
  }

  const acceptFiles = (fileList) => {
    const fileTypes = ["application/json", "text/csv"]
    const files = Array.from(fileList || []).filter((f) =>
      fileTypes.includes(f.type),
    )
    if (!files.length) {
      addAlert(AlertType.ERROR, "File Upload", "Invalid file type")
      return
    }
    removeAlertBySource("File Upload")
    setSelectedFiles(files)
    setFileName(files.map((f) => f.name).join(", "))
    setUploadProgress(0)
    setIsUploading(UploadStatus.NONE)
    setJobStatus(null)
    setFileIndex(0)
    setFileTotal(files.length)
  }

  const handleFileDrop = (e) => {
    e.preventDefault()
    acceptFiles(e.dataTransfer.files)
  }

  const handleFileChange = (event) => {
    acceptFiles(event.target.files)
  }

  const releaseReservation = async (reservationId) => {
    if (!reservationId || !schemaId) return
    try {
      await api.schemasReleaseUpload(schemaId, reservationId)
    } catch (err) {
      console.warn("Failed to release upload reservation", err)
    }
  }

  /**
   * Upload one file: reserve → jobsUpload (batch + reservation headers) →
   * wait for websocket completion → release reservation.
   */
  const uploadOneFile = async (file, index, total) => {
    setFileIndex(index + 1)
    setFileTotal(total)
    setFileName(file.name)
    setUploadProgress(0)
    setJobStatus(null)

    let reservationId = null
    let uploadedCount = 0

    try {
      const [body, fileType] = await parseFile(file)
      if (!body || !fileType) {
        throw new Error(fileError || "Failed to parse file")
      }

      uploadedCount = countRecordsFromParsed(fileType, body)
      reservationId = await api.trySchemasReserveUpload(schemaId, uploadedCount)

      const response = await api.jobsUpload(body, fileType, {
        reservationId: reservationId || undefined,
      })
      console.log("Job ID:", response.job_id)
      await api.jobsWebsocket(response.job_id)
      return uploadedCount
    } finally {
      await releaseReservation(reservationId)
    }
  }

  const uploadData = async () => {
    const files =
      selectedFiles?.length > 0
        ? selectedFiles
        : selectedFile
          ? [selectedFile]
          : []
    if (!files.length) return

    try {
      setIsUploading(UploadStatus.UPLOADING)
      removeAlertBySource("File Upload")

      let countBefore = null
      try {
        countBefore = await api.recordsSchemaCount(schemaId)
      } catch (err) {
        console.warn("Could not read pre-upload count", err)
      }

      let totalUploaded = 0
      for (let i = 0; i < files.length; i++) {
        const n = await uploadOneFile(files[i], i, files.length)
        totalUploaded += n || 0
      }

      if (countBefore != null && totalUploaded > 0) {
        try {
          const countAfter = await api.recordsSchemaCount(schemaId)
          const delta = countAfter - countBefore
          if (delta !== totalUploaded) {
            console.warn(
              `Upload count delta mismatch: expected ${totalUploaded}, got ${delta}`,
            )
          }
        } catch (err) {
          console.warn("Could not verify post-upload count", err)
        }
      }

      setIsUploading(UploadStatus.COMPLETED)
    } catch (error) {
      console.error("Error uploading data:", error)
      setIsUploading(UploadStatus.TERMINATED)
      setUploadProgress(0)
    }
  }

  //TODO(Alexandrite): handle buffered file upload for supermassive files
  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const fileContent = event.target.result

          if (file.type === "text/csv") {
            resolve([handleCSV(fileContent), file.type])
          } else {
            resolve([handleJSON(fileContent), file.type])
          }
        } catch (error) {
          setIsUploading(UploadStatus.TERMINATED)
          reject(new Error(`File parsing failed: ${error.message}`))
        }
      }

      const handleCSV = (fileContent) => {
        let csv = Papa.parse(fileContent, {
          header: false, // We need to handle the header manually.
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
          transform: (value) => value.trim(),
        })

        console.log("csv=", csv)

        if (csv.errors.length > 0) {
          throw new Error("CSV parsing errors", csv.errors)
        }

        const rows = csv.data
        let header = rows[0]

        // If the header is the same length as the rows, we need to add a schema header.
        if (header.length == rows[1].length) {
          const schemaHeader = `schema_id:${schemaId}`
          header.push(schemaHeader)
        }

        const recordCount = rows.length - 1
        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        if (maxRecordsPerSchema !== null && recordCount > maxRecordsPerSchema) {
          const errorMessage = `Upload failed: File contains ${recordCount.toLocaleString()} records but your plan supports up to ${maxRecordsPerSchema.toLocaleString()} per schema. Upgrade at ${CONTACT_SALES_URL}.`
          addAlert(AlertType.ERROR, "File Upload", errorMessage)
          throw new Error(errorMessage)
        }
        return Papa.unparse(rows)
      }

      const handleJSON = (fileContent) => {
        let json
        try {
          json = JSON.parse(fileContent)
        } catch (error) {
          if (fileContent.trim() === "") {
            addAlert(
              AlertType.ERROR,
              "File Upload",
              "File is empty, please select a different file.",
            )
            throw new Error()
          }
          addAlert(
            AlertType.ERROR,
            "File Upload",
            "Invalid JSON object, please check file format or select a different file.",
          )
          throw new Error()
        }

        // Convert to array if single object
        const records = Array.isArray(json) ? json : [json]

        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        if (
          maxRecordsPerSchema !== null &&
          records.length > maxRecordsPerSchema
        ) {
          const errorMessage = `Upload failed: File contains ${records.length.toLocaleString()} records but your plan supports up to ${maxRecordsPerSchema.toLocaleString()} per schema. Upgrade at ${CONTACT_SALES_URL}.`
          addAlert(AlertType.ERROR, "File Upload", errorMessage)
          throw new Error(errorMessage)
        }

        // If the schemaId is set (which it should be), add the schema URL to each record.
        if (schemaId) {
          const schemaUrl = `/api/schemas/${schemaId}/`
          // If the record already has a schema, skip it.
          for (let record of records) {
            if (!record?.schema) {
              record.schema = schemaUrl
            }
          }
        }

        return JSON.stringify(records)
      }

      reader.onerror = () => {
        setIsUploading(UploadStatus.TERMINATED)
        addAlert(AlertType.ERROR, "File Upload", "Failed to read file.", 4)
        reject(new Error("Failed to read file"))
      }

      reader.readAsText(file)
    })
  }

  const clearFile = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }

    // Clear states
    setIsUploading(UploadStatus.NONE)
    setUploadProgress(0)
    setJobStatus(null)
    setFileIndex(0)
    setFileTotal(0)
    if (!jobStatus) {
      setFileName("")
    }
    removeAlertBySource("File Upload")
    removeAlertBySource("Api Error")
    clearSelectedFile()
  }

  const filesSelected =
    (selectedFiles && selectedFiles.length > 0) || !!selectedFile
  const selectLabel = selectedFiles?.length
    ? selectedFiles.length === 1
      ? selectedFiles[0].name
      : `${selectedFiles.length} files selected`
    : selectedFile
      ? selectedFile.name
      : "Select File(s)"

  return (
    <Box bg="white" borderRadius="md" p="2">
      <Box
        display="flex"
        justifyContent={"center"}
        alignItems={"center"}
        height="auto"
        aspectRatio={{ base: "none", md: "1" }}
      >
        {isUploading !== UploadStatus.NONE ? (
          <Box p={8}>
            {isUploading === UploadStatus.UPLOADING ? (
              <VStack
                justifyContent={"center"}
                alignItems={"center"}
                maxW="sm"
                w="100%"
                p={4}
              >
                <Heading variant="standardMicro" mt={8}>
                  Uploading
                </Heading>
                <Text variant="base" textAlign="center" w="100%">
                  Uploading records from file:
                  <br />
                  {fileName}
                </Text>
                {fileTotal > 1 && (
                  <Text variant="base">
                    File {fileIndex} of {fileTotal}
                  </Text>
                )}
                <Text variant="base">
                  {jobStatus?.status}: {jobStatus?.processed} of{" "}
                  {jobStatus?.total} records
                </Text>
                <Text variant="base">{uploadProgress}%</Text>
                <Progress w="100%" min={0} max={100} value={uploadProgress} />
              </VStack>
            ) : (
              <VStack justifyContent={"center"} alignItems={"center"}>
                <Heading variant="standardMicro" mt={8}>
                  {isUploading === UploadStatus.TERMINATED
                    ? "Upload Terminated"
                    : "Upload Complete"}
                </Heading>
                <Text variant="base">
                  {isUploading === UploadStatus.TERMINATED
                    ? `Failed to upload records from file: ${fileName}`
                    : `Uploaded ${jobStatus?.total} records from file: ${fileName}`}
                </Text>
                <Text variant="base">{jobStatus?.status}</Text>
                {isUploading === UploadStatus.TERMINATED && (
                  <Text variant="base">{uploadProgress}%</Text>
                )}
                <Progress width="100%" value={uploadProgress} />
              </VStack>
            )}
          </Box>
        ) : (
          <VStack
            display="flex"
            height="80%"
            width={{ base: "100%", md: "80%" }}
            margin="auto"
            borderRadius="md"
            bg="Gray.100"
            textAlign="center"
            justifyContent={"center"}
            alignItems={"center"}
            border="5px dotted"
            borderColor="Gray.200"
            pt={{ base: "0", md: "auto" }}
            p={{ base: "2" }}
            _hover={{
              bg: "Gray.200",
              borderColor: "Gray.400",
            }}
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <Text variant="base">Drag and drop your data here</Text>
            <Text variant="base">or</Text>
            <HStack>
              <Button
                type="file"
                variant="primary"
                size={{ base: "sm", lg: "md" }}
                onClick={openAttachment}
              >
                {selectLabel}
              </Button>
              {filesSelected && (
                <Button
                  id="clear-file-button"
                  variant="primary"
                  size={{ base: "sm", lg: "md" }}
                  onClick={clearFile}
                >
                  ✕
                </Button>
              )}
            </HStack>
            <input
              type="file"
              accept=".json,.csv"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </VStack>
        )}
      </Box>
      {(() => {
        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        return (
          maxRecordsPerSchema !== null && (
            <Box px={4} pt={2} pb={0}>
              <Text
                variant="base"
                fontSize="sm"
                color="Gray.600"
                textAlign="center"
              >
                Your plan supports up to {maxRecordsPerSchema.toLocaleString()}{" "}
                records per schema.{" "}
                <Link
                  href={CONTACT_SALES_URL}
                  isExternal
                  textDecoration="underline"
                >
                  Upgrade your plan
                </Link>{" "}
                for more. For large loads, select multiple files (e.g. five 50K
                files) — they upload sequentially.
              </Text>
            </Box>
          )
        )
      })()}
      <HStack
        spacing={4}
        m={4}
        pt={0}
        mt={0}
        mr={{ base: 0, md: 4 }}
        justify="space-between"
      >
        <VStack spacing={2} width="fit-content"></VStack>
        <Button
          mt={{ base: 4, md: 0 }}
          variant="primary"
          size={{ base: "sm", lg: "md" }}
          onClick={uploadData}
          isDisabled={isUploading !== UploadStatus.NONE || !filesSelected}
        >
          {isUploading === UploadStatus.UPLOADING ? "Uploading..." : "Upload"}
        </Button>
      </HStack>
    </Box>
  )
}
